import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { auth } from '../services/firebase';
import { trackBeginCheckout } from '../services/trackingService';
import { PRICING, COPY, fmt } from '../config/pricing';
import { KobuLabel, KobuBackToTop } from '../components/kobu/Kobu';
import KobuNav from '../components/kobu/KobuNav';
import KobuFooter from '../components/kobu/KobuFooter';

/**
 * Pricing — Kobu editorial treatment. Parchment canvas, Gill Sans headings,
 * Fira Mono labels, hairline borders, monochrome (obsidian ink buttons, no
 * accent hue). All prices and pricing copy come from config/pricing.ts.
 */

interface Tier {
  key: string;
  name: string;
  price: string;
  cadence: string;
  blurb: string;
  features: string[];
  cta: string;
  checkoutTier?: string;     // b2c checkout tierId
  priceValue?: number;       // numeric USD value for begin_checkout/purchase events
  to?: string;               // internal link instead of checkout
  emphasis?: boolean;
  note?: string;             // small print under the CTA (guarantee, credit line…)
}

const TIERS: Tier[] = [
  {
    key: PRICING.dayPlanner.key, name: PRICING.dayPlanner.name, price: 'Free', cadence: PRICING.dayPlanner.cadence,
    blurb: 'A complete single-day itinerary for any city. No account, no card.',
    features: ['Unlimited single-day plans', 'Real, verified venues', 'Emailed to you on request'],
    cta: 'Start planning', to: '/day-planner',
  },
  {
    key: PRICING.dayPass.key, name: PRICING.dayPass.name, price: fmt(PRICING.dayPass.price), cadence: PRICING.dayPass.cadence,
    blurb: 'One premium multi-day itinerary, designed like a magazine.',
    features: ['Full multi-day itinerary', `${PRICING.dayPass.askAuraMessages} Ask Aura concierge messages`, 'Verified booking links', 'PDF & email export, no watermark'],
    cta: 'Get a Day Pass', checkoutTier: PRICING.dayPass.checkoutTier, priceValue: PRICING.dayPass.price,
    note: COPY.dayPassCredit,
  },
  {
    key: PRICING.pro.key, name: PRICING.pro.name, price: fmt(PRICING.pro.priceYearly), cadence: `per year · ${PRICING.pro.itinerariesPerYear} itineraries`,
    blurb: 'A year of extraordinary travel. The clear value if you take more than one trip.',
    features: [`${PRICING.pro.itinerariesPerYear} premium itineraries / year`, 'Unlimited Ask Aura concierge', 'Social Studio reels', 'Offline access & priority generation'],
    cta: 'Go Pro', checkoutTier: PRICING.pro.checkoutTier, priceValue: PRICING.pro.priceYearly, emphasis: true,
    note: COPY.proReassurance,
  },
  {
    key: PRICING.atelier.key, name: `${PRICING.atelier.name} · B2B`, price: fmt(PRICING.atelier.priceMonthly), cadence: `per month · ${PRICING.atelier.itinerariesPerMonth} itineraries`,
    blurb: COPY.atelierColumnBlurb,
    features: ['Your brand, zero Aura watermark', 'Concierge Studio & team access', COPY.atelierAnnualLine, COPY.hotelGroupsLine],
    cta: 'Book a demo', to: '/for-agencies',
    note: COPY.foundingColumnNote,
  },
];

const PricingPage: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startCheckout = async (tierId: string, key: string, value: number) => {
    const user = auth?.currentUser;
    if (!user) { window.location.href = '/create'; return; }
    setLoading(key); setError(null);
    try {
      const token = await user.getIdToken();
      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tierId, category: 'b2c', userEmail: user.email, userId: user.uid, origin: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok || !data.url) throw new Error(data.error || 'Checkout unavailable');
      // Fires GA4 begin_checkout + Meta InitiateCheckout and stashes tier/value
      // for the purchase event on the Stripe success redirect.
      trackBeginCheckout(key, value);
      window.location.href = data.url;
    } catch (err: any) {
      setError(err.message || 'Checkout failed — please try again.');
      setLoading(null);
    }
  };

  useSEO({
    title: `Pricing — Free AI Day Planner, ${fmt(PRICING.dayPass.price)} Day Pass, ${fmt(PRICING.pro.priceYearly)}/yr Pro | Aura Traveler`,
    description: `Plan trips free with the AI Day Planner. Upgrade to a ${fmt(PRICING.dayPass.price)} Day Pass for one premium multi-day itinerary, or Aura Pro at ${fmt(PRICING.pro.priceYearly)}/year for ${PRICING.pro.itinerariesPerYear} — a fraction of a $200–500 travel consultant.`,
    canonical: 'https://www.auratraveler.com/pricing',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'Pricing', url: 'https://www.auratraveler.com/pricing' },
    ],
  });

  return (
    <div className="min-h-screen bg-parchment text-obsidian" style={{ fontFamily: 'var(--font-gill)' }}>
      {/* Product/Offer schema — generated from config/pricing.ts (doc P0-3) */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Aura Traveler',
        description: 'AI-powered luxury travel itinerary generator. Magazine-quality multi-day itineraries with verified venues and booking links.',
        brand: { '@type': 'Brand', name: 'Aura Traveler' },
        url: 'https://www.auratraveler.com/pricing',
        offers: [
          { '@type': 'Offer', name: PRICING.dayPlanner.name, price: String(PRICING.dayPlanner.price), priceCurrency: 'USD', description: 'Unlimited free single-day itineraries.', url: 'https://www.auratraveler.com/day-planner' },
          { '@type': 'Offer', name: PRICING.dayPass.name, price: String(PRICING.dayPass.price), priceCurrency: 'USD', description: 'One premium multi-day itinerary.', url: 'https://www.auratraveler.com/pricing' },
          { '@type': 'Offer', name: PRICING.pro.name, price: String(PRICING.pro.priceYearly), priceCurrency: 'USD', description: `${PRICING.pro.itinerariesPerYear} premium itineraries per year.`, url: 'https://www.auratraveler.com/pricing' },
          { '@type': 'Offer', name: PRICING.atelier.name, price: String(PRICING.atelier.priceMonthly), priceCurrency: 'USD', description: `${PRICING.atelier.itinerariesPerMonth} white-label itineraries per month for agencies and hotels.`, url: 'https://www.auratraveler.com/for-agencies' },
        ],
      }) }} />
      <KobuNav />

      <main className="max-w-[1440px] mx-auto px-6 sm:px-10">
        {/* Hero */}
        <section className="pt-[60px] sm:pt-[110px] pb-[50px]">
          <KobuLabel>Pricing</KobuLabel>
          <h1 className="kobu-display text-[44px] sm:text-[64px] mt-5 max-w-[12ch]">Plan free. Pay for the art.</h1>
          <p className="kobu-body text-[16px] mt-6 max-w-[480px]">
            The AI Day Planner is free forever. When you want a magazine-quality multi-day
            itinerary, upgrade on your terms — no subscription required.
          </p>
        </section>

        {error && <p className="kobu-caption text-obsidian mb-6 border border-obsidian px-4 py-3 inline-block">{error}</p>}

        {/* Tiers */}
        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-obsidian border border-obsidian mb-[34px]">
          {TIERS.map((t) => (
            <div key={t.key} className="bg-parchment p-8 sm:p-10 flex flex-col">
              <div className="flex items-baseline justify-between">
                <KobuLabel className="text-ash-gray">{t.name}</KobuLabel>
                {t.emphasis && <KobuLabel className="text-obsidian">· Best value</KobuLabel>}
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="kobu-display text-[40px]">{t.price}</span>
                <span className="kobu-caption text-ash-gray">{t.cadence}</span>
              </div>
              <p className="kobu-body text-[15px] mt-4 mb-7 min-h-[44px]">{t.blurb}</p>
              <ul className="space-y-2.5 flex-grow mb-8">
                {t.features.map((f) => (
                  <li key={f} className="kobu-body text-[14px] flex gap-3">
                    <span className="text-obsidian" aria-hidden="true">—</span>{f}
                  </li>
                ))}
              </ul>
              {t.to ? (
                <Link to={t.to} className="block text-center kobu-caption border border-obsidian px-6 py-4 hover:bg-obsidian hover:text-parchment transition-colors">
                  {t.cta}
                </Link>
              ) : (
                <button
                  onClick={() => startCheckout(t.checkoutTier!, t.key, t.priceValue ?? 0)}
                  disabled={!!loading}
                  className={`kobu-caption px-6 py-4 transition-colors disabled:opacity-50 ${t.emphasis ? 'bg-obsidian text-parchment hover:bg-charcoal-deep' : 'border border-obsidian hover:bg-obsidian hover:text-parchment'}`}
                >
                  {loading === t.key ? 'Opening…' : t.cta}
                </button>
              )}
              {t.note && <p className="kobu-caption text-ash-gray mt-4 text-[12px]">{t.note}</p>}
            </div>
          ))}
        </section>

        {/* The Aura Guarantee (defined, not decorative) */}
        <p className="kobu-body text-[14px] text-graphite max-w-[640px] mb-[34px]">{COPY.guarantee}</p>

        {/* Consultant anchor */}
        <section className="border-t border-obsidian py-[50px] flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <div className="max-w-[520px]">
            <KobuLabel className="text-ash-gray">For perspective</KobuLabel>
            <p className="kobu-heading text-[24px] sm:text-[28px] mt-4">
              {COPY.anchor}
            </p>
          </div>
        </section>

        {/* B2B band */}
        <section className="border-t border-obsidian py-[50px] flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <KobuLabel className="text-ash-gray">Hotels & agencies</KobuLabel>
            <p className="kobu-heading text-[24px] sm:text-[28px] mt-4 max-w-[20ch]">White-label itineraries for your property or practice.</p>
            <p className="kobu-caption text-ash-gray mt-3">{COPY.atelierBanner} · {COPY.enterpriseBanner}</p>
          </div>
          <Link to="/for-agencies" className="kobu-heading text-[18px] sm:text-[21px] hover:text-ash-gray transition-colors inline-flex items-center gap-2 shrink-0">
            Talk to us <span aria-hidden="true">↗</span>
          </Link>
        </section>
      </main>

      <KobuFooter />
      <KobuBackToTop />
    </div>
  );
};

export default PricingPage;
